import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function AdminCalendario() {
    const [config, setConfig] = useState(null);
    const [startDate, setStartDate] = useState("2026-09-15");
    const [giornatePerWindow, setGiornatePerWindow] = useState(1);
    const [windowDays, setWindowDays] = useState(4);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const [tavoliniMsg, setTavoliniMsg] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/season/config");
            setConfig(data);
            if (data.start_date) setStartDate(data.start_date);
            if (data.giornate_per_window) setGiornatePerWindow(data.giornate_per_window);
            if (data.window_days) setWindowDays(data.window_days);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        load();
    }, []);

    // Live preview when changing date or cadence (no save)
    const previewWith = async (d, gpw, wd) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
        try {
            const { data } = await api.get(
                `/season/config?start_date=${d}&giornate_per_window=${gpw}&window_days=${wd}`
            );
            setConfig((c) => ({ ...(c || {}), preview: data.preview, spacing_days: data.spacing_days }));
        } catch {
            /* ignore */
        }
    };

    const onDateChange = (d) => { setStartDate(d); previewWith(d, giornatePerWindow, windowDays); };
    const onGpwChange = (v) => {
        const n = Math.max(1, parseInt(v || "1", 10));
        setGiornatePerWindow(n); previewWith(startDate, n, windowDays);
    };
    const onWdChange = (v) => {
        const n = Math.max(1, parseInt(v || "1", 10));
        setWindowDays(n); previewWith(startDate, giornatePerWindow, n);
    };

    const applyDate = async () => {
        setBusy(true);
        setErr("");
        setMsg("");
        try {
            const { data } = await api.put("/season/start-date", {
                start_date: startDate,
                giornate_per_window: giornatePerWindow,
                window_days: windowDays,
            });
            setMsg(
                `Date applicate: ${data.league_matches_dated} partite di campionato, ${data.cup_rounds_dated} turni di coppa. Cadenza: ${data.giornate_per_window} giornata/e ogni ${data.window_days} giorni (≈ ${data.spacing_days} gg/giornata).`
            );
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const runTavolini = async () => {
        setBusy(true);
        setTavoliniMsg("");
        setErr("");
        try {
            const { data } = await api.post("/season/process-tavolini");
            setTavoliniMsg(`Tavolini assegnati: ${data.assigned} partite scadute.`);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const preview = config?.preview || [];

    return (
        <div data-testid="admin-calendario">
            <div className="op-label text-t2bb6ff mb-2">// CALENDARIO STAGIONE</div>
            <h2 className="op-heading text-3xl mb-2">Date & Giornate</h2>
            <p className="text-t8f95a3 text-sm mb-6 max-w-2xl">
                Imposta la data d'inizio e la cadenza: scegli <strong>quante giornate</strong> giocare
                <strong> ogni quanti giorni</strong> (es. 1 ogni 4, 1 ogni 5, 2 ogni 6, 3 ogni 10).
                Il tabellino resta inseribile dai presidenti per la giornata corrente + la successiva,
                poi scatta il tavolino automatico. Le date vengono applicate a tutte le competizioni
                (campionato + coppe), con le finali nell'ultima settimana.
            </p>

            <div className="op-card p-5 mb-6">
                <div className="flex flex-wrap items-end gap-4">
                    <div>
                        <label className="op-label text-[10px] block mb-1">Data inizio stagione</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => onDateChange(e.target.value)}
                            className="bg-t0d0f12 border border-t22252a px-3 py-2 op-mono text-tffffff text-sm focus:border-t2bb6ff focus:outline-none"
                            data-testid="season-start-date-input"
                        />
                    </div>
                    <div>
                        <label className="op-label text-[10px] block mb-1">Giornate</label>
                        <input
                            type="number"
                            min={1}
                            value={giornatePerWindow}
                            onChange={(e) => onGpwChange(e.target.value)}
                            className="bg-t0d0f12 border border-t22252a px-3 py-2 op-mono text-tffffff text-sm w-20 focus:border-t2bb6ff focus:outline-none"
                            data-testid="season-giornate-per-window-input"
                        />
                    </div>
                    <div className="op-label text-[10px] text-t5c6370 pb-3">ogni</div>
                    <div>
                        <label className="op-label text-[10px] block mb-1">Giorni</label>
                        <input
                            type="number"
                            min={1}
                            value={windowDays}
                            onChange={(e) => onWdChange(e.target.value)}
                            className="bg-t0d0f12 border border-t22252a px-3 py-2 op-mono text-tffffff text-sm w-20 focus:border-t2bb6ff focus:outline-none"
                            data-testid="season-window-days-input"
                        />
                    </div>
                    <button
                        onClick={applyDate}
                        disabled={busy}
                        className="op-btn op-btn-primary disabled:opacity-50"
                        data-testid="season-apply-date-btn"
                    >
                        {busy ? "Applicazione…" : "Imposta data e genera le date"}
                    </button>
                    <button
                        onClick={runTavolini}
                        disabled={busy}
                        className="op-btn disabled:opacity-50"
                        data-testid="season-process-tavolini-btn"
                        title="Assegna un risultato d'ufficio (casuale) alle partite scadute non registrate"
                    >
                        ⚖️ Processa tavolini scaduti
                    </button>
                </div>
                {config?.spacing_days != null && (
                    <div className="op-mono text-xs text-t8f95a3 mt-3" data-testid="season-cadence-hint">
                        Cadenza: {giornatePerWindow} giornata/e ogni {windowDays} giorni · ≈ {config.spacing_days} giorni per giornata
                    </div>
                )}
                {config?.start_date && (
                    <div className="op-mono text-xs text-t7ce38b mt-3" data-testid="season-current-start">
                        Data d'inizio attuale: {config.start_date}
                    </div>
                )}
                {msg && <div className="op-mono text-xs text-t7ce38b mt-2" data-testid="season-apply-msg">{msg}</div>}
                {tavoliniMsg && <div className="op-mono text-xs text-te8ff00 mt-2" data-testid="season-tavolini-msg">{tavoliniMsg}</div>}
                {err && <div className="op-mono text-xs text-tff4d4d mt-2">{err}</div>}
            </div>

            {preview.length > 0 && (
                <div className="op-card p-0 overflow-hidden" data-testid="season-preview">
                    <div className="px-5 py-3 border-b border-t22252a op-label text-t2bb6ff">
                        // ANTEPRIMA GIORNATE ({preview.length})
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="op-label text-[10px] text-t8f95a3 border-b border-t22252a">
                                    <th className="text-left px-5 py-2">Giornata</th>
                                    <th className="text-left px-3 py-2">Dal</th>
                                    <th className="text-left px-3 py-2">Al</th>
                                    <th className="text-left px-3 py-2">Termine tabellino</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.map((g) => (
                                    <tr
                                        key={g.giornata}
                                        className="border-b border-t16181d hover:bg-t101216"
                                        data-testid={`season-preview-row-${g.giornata}`}
                                    >
                                        <td className="px-5 py-2 op-mono text-tffffff">G{g.giornata}</td>
                                        <td className="px-3 py-2 op-mono text-tc9ccd3">{g.date_from}</td>
                                        <td className="px-3 py-2 op-mono text-tc9ccd3">{g.date_to}</td>
                                        <td className="px-3 py-2 op-mono text-tff7a1a">{g.deadline}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
